import { Vehicle } from '../../../crud-vehicle-module-cars/vehicle';

/**
 * Estado de telemetría en vivo (semáforo del mapa):
 *  - MOVING  → Verde  (en movimiento)
 *  - STOPPED → Rojo   (detenido / apagado)
 *  - IDLE    → Amarillo (ralentí / taller)
 */
export type FleetStatus = 'MOVING' | 'STOPPED' | 'IDLE';

export interface Telemetry {
  lat: number;
  lng: number;
  speedKmh: number;
  heading: number; // grados 0-360 para rotar el ícono
  fuelPct: number; // nivel de combustible 0-100
  odometerKm: number;
  updatedAt: string; // ISO
}

export interface Driver {
  id: string;
  name: string;
  avatar?: string;
  phone?: string;
  license?: string;
}

export type MaintenanceStatus = 'DONE' | 'UPCOMING' | 'OVERDUE';

export interface MaintenanceRecord {
  id: string;
  date: string; // ISO
  type: string; // 'Cambio de aceite', 'Frenos', ...
  km: number;
  cost?: number;
  status: MaintenanceStatus;
  workshop?: string;
  alert?: boolean; // próximo/vencido → alerta visible
}

export interface RepairRecord {
  id: string;
  date: string; // ISO
  title: string;
  description: string;
  cost: number;
  workshop?: string;
  severity: 'low' | 'medium' | 'high';
}

export type ExpenseCategory = 'fuel' | 'maintenance' | 'repair' | 'insurance' | 'toll' | 'other';

export interface ExpenseRecord {
  id: string;
  date: string; // ISO
  category: ExpenseCategory;
  concept: string;
  amount: number;
}

export interface DriverAssignment {
  driver: Driver;
  from: string; // ISO
  to?: string; // ISO | undefined = actual
  distanceKm: number;
}

export interface VehicleAnalytics {
  fuelEfficiencyKmpl: number; // rendimiento km/galón (o km/l)
  idleTimeHours: number; // tiempo inactividad
  costPerKm: number; // costo por km
  driverScore: number; // 0-100
  driverScoreSeries: number[]; // 12 meses
  behavior: {
    braking: number;
    acceleration: number;
    cornering: number;
    speeding: number;
  };
  monthlyFuelCost: number[]; // 12 meses
  distanceMonthKm: number;
}

/**
 * FleetVehicle = el `Vehicle` real del CRUD extendido con la capa de
 * telemetría + perfil 360. Es el ÚNICO objeto fuente de verdad: tabla,
 * mapa, mini-tarjeta, analíticas y perfil leen de aquí. La coherencia de
 * mocks está garantizada por construcción.
 */
export interface FleetVehicle extends Vehicle {
  status_live: FleetStatus;
  telemetry: Telemetry;
  driver: Driver;
  addressLabel: string; // ubicación legible para la mini-tarjeta
  maintenance: MaintenanceRecord[];
  repairs: RepairRecord[];
  expenses: ExpenseRecord[];
  driverHistory: DriverAssignment[];
  analytics: VehicleAnalytics;
}

/** KPIs agregados de toda la flota, derivados del store. */
export interface FleetKpis {
  total: number;
  moving: number;
  stopped: number;
  idle: number;
  avgFuelEfficiency: number;
  totalIdleHours: number;
  avgCostPerKm: number;
  maintenanceAlerts: number;
  fuelCostSeries: number[]; // suma mensual de la flota (12)
  costliestPerKm: { plate: string; label: string; costPerKm: number }[];
}

/** Metadatos de presentación por estado (color semáforo reutilizable). */
export const FLEET_STATUS_META: Record<FleetStatus, { label: string; color: string; hex: string }> = {
  MOVING: { label: 'En movimiento', color: 'text-emerald-500', hex: '#10b981' },
  STOPPED: { label: 'Detenido', color: 'text-red-500', hex: '#ef4444' },
  IDLE: { label: 'En ralentí / Taller', color: 'text-amber-500', hex: '#f59e0b' },
};
